import React from 'react';

import classNames from 'classnames';

import ProportionBar from './ProportionBar';

import { formatFigure, formatMoney, ZERO_DASH } from 'Helpers/formatService';

/**
 * IB-757 — Budget Versions v2 (compare polish).
 *
 * One money column (Revenue | Expenditure | Net) for a version row: the total,
 * a committed-proportion bar (Revenue/Expenditure only), and TWO movement rows:
 *
 *   - "VS PRIOR" — ALWAYS shown: movement vs the immediately-preceding version.
 *   - "VS BASE"  — movement vs the dropdown-selected base version; shown ONLY
 *     when a specific base is selected (in the default "Previous version" mode it
 *     would just duplicate VS PRIOR, so it is hidden).
 *
 * Movement carries a semantic colour (rising revenue/net = green, rising
 * expenditure = red); a negligible move renders neutrally with a "■". Sentinel
 * strings ("— new" for the earliest version, "—" for the selected base itself)
 * render as plain neutral text.
 *
 * `metric` selects which figure (revenue|expenditure|net) this cell shows.
 * `comparison` is the dual object { prior, base } from buildDualComparisons.
 */

// formatMoney shows up to three decimals.
const formatMoneyFigure = (value) => formatFigure(value, formatMoney, { precision: 3 });

/**
 * A signed, one-decimal percentage, e.g. "(+7.0%)" / "(-12.3%)"; "–" for zero and
 * "(+<0.1%)" for a move that rounds to zero. Null when there is nothing
 * meaningful to show — no numeric value (a zero-base divide-by-zero guard) — so
 * the caller renders the value alone without a "%".
 */
function formatPercentage(pct) {
    if (typeof pct !== 'number' || !isFinite(pct)) {
        return null;
    }

    const magnitude = formatFigure(Math.abs(pct), (value, precision) => value.toFixed(precision), { precision: 1 });

    return magnitude === ZERO_DASH ? ZERO_DASH : `(${pct > 0 ? '+' : '-'}${magnitude}%)`;
}

/**
 * A single movement row: a small grey uppercase label on the left and the
 * movement value on the right. `movement` is either the diff object for this
 * row, a sentinel string, or null/undefined (nothing to render).
 */
function MovementRow({ label, movement, metric, leftAlign }) {
    // No movement to show (e.g. VS BASE in the default "Previous version" mode).
    if (movement === null || movement === undefined) {
        return null;
    }

    // Net position has no committed bar, so justify-between strands the movement
    // at the far right of the column. `leftAlign` keeps the label and value
    // together on the left with a small gap. (v2 polish)
    const rowClass = classNames('tw-flex tw-items-baseline tw-gap-x-2', leftAlign ? 'tw-justify-start' : 'tw-justify-between');

    // Sentinel string ("— new" | "—") — neutral muted text.
    if (typeof movement === 'string') {
        return (
            <div className={rowClass}>
                <span className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{label}</span>
                <span className="tw-text-xs tw-text-charcoal-50">{movement}</span>
            </div>
        );
    }

    if (typeof movement[metric] !== 'number') {
        return null;
    }

    const diff = movement[metric];
    const isPositive = movement[`${metric}_is_positive`];
    const pctLabel = formatPercentage(movement[`${metric}_pct`]);
    const diffText = formatMoneyFigure(Math.abs(diff));
    const isNegligible = diffText === ZERO_DASH;
    const arrow = isNegligible ? '■' : diff > 0 ? '▲' : '▼';
    const colourClass = isNegligible ? 'tw-text-charcoal-50' : isPositive ? 'tw-text-green' : 'tw-text-red';

    return (
        <div className={rowClass}>
            <span className="tw-text-2xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50">{label}</span>
            <span className={classNames('tw-flex tw-items-baseline tw-gap-x-1 tw-text-xs tw-font-semibold', colourClass)}>
                <span>{arrow}</span>
                <span>{diffText}</span>
                {pctLabel && <span className="tw-font-normal tw-opacity-80">{pctLabel}</span>}
            </span>
        </div>
    );
}

export default function MetricCell({ metric, metrics, comparison }) {
    if (!metrics) {
        return <td />;
    }

    // A version whose creation clone failed part-way has no FinancialStats row,
    // so its money figures are unknown — not zero. Render a single neutral "No
    // snapshot data" label once per row (under Net) instead of misleading €0s
    // with a 0% committed bar. (IB-756)
    if (metrics.has_financials === false) {
        if (metric !== 'net') {
            return <td className="tw-align-middle" />;
        }

        return (
            <td className="tw-align-middle">
                <div className="tw-text-xs tw-text-charcoal-50 tw-italic">{$t('No snapshot data')}</div>
            </td>
        );
    }

    const isNet = metric === 'net';
    const total = metrics[metric];
    const proportion = isNet ? null : metrics[`committed_${metric}_proportion`];
    const committed = isNet ? null : metrics[`committed_${metric}`];

    // Net carries a sign and a colour only when it renders as a figure, never on the zero dash.
    const totalText = formatMoneyFigure(isNet ? Math.abs(total) : total);
    const signedNet = isNet && totalText !== ZERO_DASH && totalText !== '';
    const totalDisplay = signedNet ? `${total > 0 ? '+' : '−'}${totalText}` : totalText;
    const totalColour = signedNet ? (total > 0 ? 'tw-text-green' : 'tw-text-red') : 'tw-text-charcoal';

    const prior = comparison ? comparison.prior : null;
    const base = comparison ? comparison.base : null;

    return (
        <td className="tw-align-middle">
            <div className="tw-flex tw-flex-col tw-gap-y-1">
                <div className={classNames('tw-text-sm tw-font-semibold', totalColour)}>{totalDisplay}</div>
                {typeof proportion === 'number' && (
                    <div className="tw-flex tw-flex-col tw-gap-y-0.5">
                        <ProportionBar proportion={proportion} />
                        {typeof committed === 'number' && (
                            <span className="tw-text-2xs tw-text-charcoal-50">{$t('{{amount}} committed', { amount: formatMoney(committed) })}</span>
                        )}
                    </div>
                )}
                <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-y-0.5">
                    <MovementRow label={$t('vs prior')} movement={prior} metric={metric} leftAlign={isNet} />
                    <MovementRow label={$t('vs base')} movement={base} metric={metric} leftAlign={isNet} />
                </div>
            </div>
        </td>
    );
}
